import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SERVICES, scrollToId } from "@/data";
import ServiceCursor from "@/components/ServiceCursor";

const reveal = {
    initial: { opacity: 0, y: 28 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "0px 0px -20px 0px" },
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
};

function ServicePanel({ service, i, onHover }) {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

    return (
        <div
            ref={ref}
            onMouseEnter={() => onHover(service.cursor)}
            onMouseLeave={() => onHover(null)}
            className={`service-panel flex flex-col ${i === 1 ? "lg:pl-12 xl:pl-16" : "lg:pr-12 xl:pr-16"}`}
            data-testid={`service-panel-${service.number}`}
        >
            <motion.div
                initial={{ clipPath: "inset(10% 6% 10% 6%)" }}
                whileInView={{ clipPath: "inset(0% 0% 0% 0%)" }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 1.2, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                className="relative aspect-[16/9] overflow-hidden lg:aspect-auto lg:h-[21vh] lg:min-h-[150px] [@media(max-height:820px)]:lg:h-[15vh] [@media(max-height:820px)]:lg:min-h-0"
            >
                {service.video_url ? (
                    <motion.video
                        data-testid={`service-video-${service.number}`}
                        src={service.video_url}
                        aria-label={service.title}
                        autoPlay
                        muted
                        loop
                        playsInline
                        style={{ y }}
                        className="absolute inset-0 h-[116%] w-full object-cover"
                    />
                ) : (
                    <motion.img
                        data-testid={`service-pan-image-${service.number}`}
                        src={service.image_url}
                        alt={service.title}
                        style={{ y }}
                        animate={{ x: ["0%", "-16.6667%", "0%"] }}
                        transition={{ duration: 100, repeat: Infinity, ease: "easeInOut" }}
                        className="absolute inset-0 h-[116%] w-[120%] max-w-none object-cover"
                    />
                )}
            </motion.div>

            <div className="mt-7 lg:mt-6">
                <motion.p
                    {...reveal}
                    className="font-mono2 text-[10px] uppercase tracking-[0.34em] text-[#6B6862]"
                >
                    {service.number} — {service.tag}
                </motion.p>
                <motion.h3
                    {...reveal}
                    transition={{ ...reveal.transition, delay: 0.08 }}
                    className="mt-3 font-display text-3xl font-light leading-[1.05] tracking-tight text-[#121211] sm:text-4xl xl:text-[2.6rem]"
                    data-testid={`service-title-${service.number}`}
                >
                    {service.title}
                    <span className="iridescent-text">.</span>
                </motion.h3>
                <motion.p
                    {...reveal}
                    transition={{ ...reveal.transition, delay: 0.14 }}
                    className="mt-2 text-xs font-medium uppercase tracking-[0.14em] text-[#6B6862]"
                >
                    {service.subtitle}
                </motion.p>
                <motion.p
                    {...reveal}
                    transition={{ ...reveal.transition, delay: 0.2 }}
                    className="mt-4 max-w-xl text-sm font-light leading-relaxed text-[#121211]/75 [@media(max-height:820px)]:lg:text-[13px]"
                >
                    {service.description}
                </motion.p>
                <motion.ul
                    {...reveal}
                    transition={{ ...reveal.transition, delay: 0.26 }}
                    className="mt-6 divide-y divide-[#121211]/10 border-y border-[#121211]/10"
                    data-testid={`service-details-${service.number}`}
                >
                    {service.details.map((d) => (
                        <li
                            key={d}
                            className="flex items-center justify-between gap-6 py-2.5 font-mono2 text-[11px] uppercase tracking-[0.16em] text-[#121211] lg:py-[0.65rem] lg:text-xs [@media(max-height:820px)]:lg:py-2 [@media(max-height:820px)]:lg:text-[11px]"
                        >
                            {d}
                            <span className="text-[#121211]/50">+</span>
                        </li>
                    ))}
                </motion.ul>
                <motion.button
                    {...reveal}
                    transition={{ ...reveal.transition, delay: 0.32 }}
                    data-testid={`service-cta-${service.number}`}
                    onClick={() => scrollToId("contact")}
                    className="project-cta group mt-6 inline-flex items-center gap-3 rounded-full bg-[#121211] px-6 py-3 font-mono2 text-[11px] uppercase tracking-[0.24em] text-[#FBF9F5]"
                >
                    Start a project
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </motion.button>
            </div>
        </div>
    );
}

export default function Services() {
    const [cursor, setCursor] = useState(null);
    return (
        <section
            id="services"
            className="relative bg-[#FBF9F5] px-6 lg:flex lg:min-h-screen lg:flex-col lg:justify-center lg:px-10"
            data-testid="services-section"
        >
            <div className="mx-auto w-full max-w-[1400px] py-24 lg:py-0 lg:pb-8 lg:pt-20 [@media(max-height:820px)]:lg:pt-[4.5rem]">
                <div className="flex flex-col justify-between gap-4 border-b border-[#121211]/10 pb-6 lg:flex-row lg:items-end">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-baseline lg:gap-8">
                        <motion.p
                            {...reveal}
                            className="font-mono2 text-[10px] uppercase tracking-[0.34em] text-[#6B6862]"
                        >
                            What we do
                        </motion.p>
                        <motion.h2
                            {...reveal}
                            transition={{ ...reveal.transition, delay: 0.08 }}
                            className="font-display text-4xl font-light leading-[1.02] tracking-tight sm:text-5xl"
                        >
                            Two capabilities.{" "}
                            <span className="italic">One visual language.</span>
                        </motion.h2>
                    </div>
                    <motion.p
                        {...reveal}
                        transition={{ ...reveal.transition, delay: 0.16 }}
                        className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#6B6862]"
                        data-testid="services-progression"
                    >
                        Immersive 360° · Aerial & Drone
                    </motion.p>
                </div>

                <div
                    className="relative grid gap-y-20 pt-10 lg:grid-cols-2 lg:gap-y-0 lg:pt-10"
                    data-testid="services-grid"
                >
                    <div className="absolute bottom-0 left-1/2 top-10 hidden w-px bg-[#121211]/10 lg:block" />
                    {SERVICES.map((s, i) => (
                        <ServicePanel key={s.number} service={s} i={i} onHover={setCursor} />
                    ))}
                </div>
            </div>
            <ServiceCursor mode={cursor} />
        </section>
    );
}
